import { createSlice, current } from "@reduxjs/toolkit";

import {
  getUnitThunk,
  createUnitThunk,
  updateUnitThunk,
  deleteUnitThunk,
  getUnitByMitraThunk,
  bulkCreateUnitThunk,
} from "state/action/unit";

// Helper
const unitKey = (p, b, f, lease, strata) =>
  `${p || ""}|${b || ""}|${f || ""}|${lease || ""}|${strata || ""}`;

const parseParams = (metaArg) => {
  const qp = (metaArg?.queryParams || "").replace(/^\?/, "");
  const params = new URLSearchParams(qp);

  // Handle multiple lease_status params
  const leaseStatusAll = params.getAll("lease_status");
  const leaseStatus =
    leaseStatusAll.length > 0
      ? leaseStatusAll.sort().join(",")
      : params.get("lease_status") || "";

  return {
    id: params.get("id") || "",
    property: params.get("property") || "",
    building: params.get("building") || "",
    floor: params.get("floor") || "",
    lease_status: leaseStatus,
    strata_status: params.get("strata_status") || "",
  };
};

// Initial state
const initialState = {
  // Get Unit inisial state
  getUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  getUnitByMitra: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  byPBF: {}, // Pastikan byPBF diinisialisasi sebagai objek
  byId: {},
  // Create Unit inisial state
  createUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  getUnitDetail: {
    data: null,
    isPending: false,
    isFulfilled: false,
    isRejected: false,
    err: null,
  },
  // Update Unit inisial state
  updateUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Delete Unit inisial state
  deleteUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  // Bulk Create Unit inisial state
  bulkCreateUnit: {
    data: null,
    isFulfilled: false,
    isPending: false,
    isRejected: false,
    err: null,
  },
  createUnitInputBody: {
    property: "",
    building: "",
    floor: "",
    unit_number: "",
    flag: "",
    unit_category: "",
    lease_status: "",
    isUsingCT: false,
    strata_status: "",
    utility: [],
    basePriceItems: [],
    isCustomBasePrice: false,
  },
  // Unit object value
  unit_object_value: "",
  selectedUnit: {
    property: "",
    building: "",
    floor: "",
    unit_number: "",
    flag: "",
    unit_category: "",
    lease_status: "",
    strata_status: "",
    unit_type: "",
    isUsingCT: false,
    utility: [],
    basePriceItems: [],
    isCustomBasePrice: false,
    tenant: null,
    description: "",
  },
};

export const unitSlice = createSlice({
  name: "unit",
  initialState,
  reducers: {
    setUnitField: (state, action) => {
      const { field, value } = action.payload;
      state.createUnitInputBody[field] = value;
    },
    // 2. Tambah blok Utility baru
    addUtilityItem: (state) => {
      state.createUnitInputBody.utility.push({
        utility: "",
        meter_no: "",
        isLumpsum: false,
        pipe_size: "",
        tarif_category: "",
        installed_power_va: "",
      });
    },

    // 3. Update data di dalam satu blok utility spesifik
    updateUtilityItem: (state, action) => {
      const { index, field, value } = action.payload;
      const utilityItem = state.createUnitInputBody.utility[index];

      if (utilityItem) {
        utilityItem[field] = value;

        // Pembersihan field dinamis:
        // Hapus property yang tidak relevan jika jenis utility diganti
        if (field === "utility") {
          if (value === "electricity") {
            delete utilityItem.pipe_size;
            delete utilityItem.tarif_category;
          } else if (value === "water") {
            delete utilityItem.installed_power_va;
          }
        }
      }
    },

    // 4. Hapus utility dari list
    removeUtilityItem: (state, action) => {
      const index = action.payload;
      state.createUnitInputBody.utility.splice(index, 1);
    },
    // Actions untuk Array BasePriceItems
    addBasePriceItem: (state) => {
      state.createUnitInputBody.basePriceItems.push({
        charge: "",
        currency: "IDR",
        price: "",
        frequency: "Monthly",
        measurement: "sqm",
      });
    },
    removeBasePriceItem: (state, action) => {
      state.createUnitInputBody.basePriceItems.splice(action.payload, 1);
    },
    updateBasePriceItem: (state, action) => {
      const { index, field, value } = action.payload;
      state.createUnitInputBody.basePriceItems[index][field] = value;
    },
    resetUnitForm: (state) => {
      state.createUnitInputBody = initialState.createUnitInputBody;
    },
    setGetUnitObjectValue: (state, action) => {
      return {
        ...state,
        unit_object_value: action.payload,
      };
    },
    // note: clear state
    setClearGetUnitObjectValue: (state, _) => {
      return {
        ...state,
        unit_object_value: "",
      };
    },
    addUtility: (state, action) => {
      // const { index, field, value } = action.payload;
      // const items = state.createUnitInputBody.items;
      const items = state.createUnitInputBody.utility;

      // if (items[index]) {
      //   items[index][field] = value;
      // }
      items.push({ utility: "", meter_no: "" });
    },

    updateUtilityField: (state, action) => {
      const { index, name, value } = action.payload;

      state.createUnitInputBody.utility[index][name] = value;
    },
    setClearGetUnit: (state, _) => {
      return {
        ...state,
        getUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearUnitPbfCache: (state, _) => {
      state.byPBF = {};
    },
    setClearUnitPbfByKey: (state, action) => {
      const {
        property,
        building,
        floor,
        lease_status = "",
        strata_status = "",
      } = action.payload || {};
      const key = unitKey(
        property,
        building,
        floor,
        lease_status,
        strata_status,
      );
      delete state.byPBF[key];
    },
    setClearUnitById: (state, action) => {
      const id = action.payload;
      delete state.byId[id];
    },

    setClearCreateUnit: (state, _) => {
      return {
        ...state,
        createUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setSelectedUnit: (state, action) => {
      return {
        ...state,
        selectedUnit: action.payload,
      };
    },
    // note: clear state
    setCreateUnitInputBody: (state, action) => {
      return {
        ...state,
        createUnitInputBody: action.payload,
      };
    },
    setClearCreateUnitInputBody: (state, _) => {
      return {
        ...state,
        createUnitInputBody: {
          property: "",
          building: "",
          floor: "",
          unit_number: "",
          flag: "",
          unit_category: "",
          lease_status: "",
          strata_status: "",
          unit_type: "",
          tenant: null,
          utility: [{ utility: "", meter_no: "", isLumpsum: false }],
          basePriceItems: [],
          isCustomBasePrice: false,
          isUsingCT: false,
        },
      };
    },

    setClearUpdateUnit: (state, _) => {
      return {
        ...state,
        updateUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearDeleteUnit: (state, _) => {
      return {
        ...state,
        deleteUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
      };
    },
    setClearAllUnitState: (state, _) => {
      return {
        ...state,
        // Get Unit inisial state
        getUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        byPBF: {},
        byId: {},
        // Create Unit inisial state
        createUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Update Unit inisial state
        updateUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        // Delete Unit inisial state
        deleteUnit: {
          data: null,
          isFulfilled: false,
          isPending: false,
          isRejected: false,
          err: null,
        },
        createUnitInputBody: {
          property: "",
          building: "",
          floor: "",
          unit_number: "",
          daya: "",
          flag: "",
          unit_category: "",
          lease_status: "",
          strata_status: "",
          unit_type: "",
          aggrement_unit: "",
          occupiable_area: "",
          common_area: "",
          gross_area: "",
          tenant: null,
          description: "",
          start_date: null,
          end_date: null,
        },
        // Unit object value
        unit_object_value: "",
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get Unit reducer
      .addCase(getUnitThunk.pending, (state, _) => {
        return {
          ...state,
          getUnit: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getUnitThunk.fulfilled, (state, action) => {
        state.getUnit = {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        };

        const { id, property, building, floor, lease_status, strata_status } =
          parseParams(action.meta.arg);

        if (id) {
          state.byId[id] = action.payload;
        } else {
          const key = unitKey(
            property,
            building,
            floor,
            lease_status,
            strata_status,
          );
          state.byPBF[key] = action.payload;
        }
      })
      .addCase(getUnitThunk.rejected, (state, action) => {
        state.getUnit = {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        };
      })
      .addCase(getUnitByMitraThunk.pending, (state, _) => {
        return {
          ...state,
          getUnitByMitra: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(getUnitByMitraThunk.fulfilled, (state, action) => {
        state.getUnitByMitra = {
          data: action.payload,
          isPending: false,
          isFulfilled: true,
          isRejected: false,
          err: null,
        };

        const { id, property, building, floor, lease_status, strata_status } =
          parseParams(action.meta.arg);

        if (id) {
          state.byId[id] = action.payload;
        } else {
          const key = unitKey(
            property,
            building,
            floor,
            lease_status,
            strata_status,
          );
          state.byPBF[key] = action.payload;
        }
      })
      .addCase(getUnitByMitraThunk.rejected, (state, action) => {
        state.getUnitByMitra = {
          data: null,
          isPending: false,
          isFulfilled: false,
          isRejected: true,
          err: action.error.message,
        };
      })
      // Create Unit reducer
      .addCase(createUnitThunk.pending, (state, _) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createUnitThunk.fulfilled, (state, action) => {
        return {
          ...state,
          register: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(createUnitThunk.rejected, (state, action) => {
        return {
          ...state,
          register: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // update Unit reducer
      .addCase(updateUnitThunk.pending, (state, _) => {
        return {
          ...state,
          updateUnit: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateUnitThunk.fulfilled, (state, action) => {
        return {
          ...state,
          updateUnit: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(updateUnitThunk.rejected, (state, action) => {
        return {
          ...state,
          updateUnit: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      })
      // Bulk Create Unit reducer
      .addCase(bulkCreateUnitThunk.pending, (state, _) => {
        return {
          ...state,
          bulkCreateUnit: {
            data: null,
            isPending: true,
            isFulfilled: false,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(bulkCreateUnitThunk.fulfilled, (state, action) => {
        return {
          ...state,
          bulkCreateUnit: {
            data: action.payload,
            isPending: false,
            isFulfilled: true,
            isRejected: false,
            err: null,
          },
        };
      })
      .addCase(bulkCreateUnitThunk.rejected, (state, action) => {
        return {
          ...state,
          bulkCreateUnit: {
            data: null,
            isPending: false,
            isFulfilled: false,
            isRejected: true,
            err: action.error.message,
          },
        };
      });
  },
});

export const unitAction = {
  ...unitSlice.actions,
  getUnitThunk,
  createUnitThunk,
  updateUnitThunk,
  deleteUnitThunk,
  getUnitByMitraThunk,
  bulkCreateUnitThunk,
};

export function selectUnitObjectValue(state) {
  return state?.unit?.unit_object_value;
}

export function selectUnit(state) {
  return state?.unit;
}

export const selectUnitsByPBF =
  (property, building, floor, lease = "Vacant", strata = "Available") =>
  (state) =>
    state.unit.byPBF[unitKey(property, building, floor, lease, strata)]
      ?.data || [];

export const selectUnitsById = (id) => (state) =>
  state.unit.byId[id]?.data || [];

export default unitSlice.reducer;
